import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaWhatsapp, FaTimes, FaUser, FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaNotesMedical } from 'react-icons/fa';

const PatientInfoModal = ({ show, onHide, onSubmit, type = 'regular', title = 'Patient Information' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    age: '',
    problem: '',
    painLevel: '',
    preferredDate: '',
    preferredTime: '',
    location: '',
    previousTreatment: '',
    emergency: '',
    budget: '',
    availability: '',
    questions: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Common validations
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    // Type-specific validations
    if (type === 'regular' || type === 'urgent') {
      if (!formData.problem.trim()) {
        newErrors.problem = 'Please describe your problem';
      }
      if (!formData.preferredDate) {
        newErrors.preferredDate = 'Please select a preferred date';
      }
    }

    if (type === 'emergency') {
      if (!formData.emergency.trim()) {
        newErrors.emergency = 'Please describe the emergency situation';
      }
      if (!formData.location.trim()) {
        newErrors.location = 'Location is required for emergency';
      }
    }

    if (type === 'consultation') {
      if (!formData.problem.trim()) {
        newErrors.problem = 'Please describe your situation';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Generate WhatsApp message based on type
    let message = '';
    const phone = formData.phone.replace(/\s/g, '');
    
    switch (type) {
      case 'regular':
        message = `📅 *APPOINTMENT BOOKING REQUEST*
Hi Dr. Pritish! I would like to book a physiotherapy appointment.

👤 *Patient Details:*
- Name: ${formData.name}
- Phone: ${phone}
- Age: ${formData.age || 'Not specified'}

🏥 *Treatment Details:*
- Problem Area: ${formData.problem}
- Pain Level (1-10): ${formData.painLevel || 'Not specified'}
- Preferred Date: ${formData.preferredDate}
- Preferred Time: ${formData.preferredTime || 'Flexible'}
- Previous Treatment: ${formData.previousTreatment || 'None mentioned'}

Looking forward to your treatment! 🙏`;
        break;

      case 'urgent':
        message = `⚡ *URGENT APPOINTMENT REQUEST*
Hi Dr. Pritish! I need an urgent physiotherapy appointment.

👤 *Patient Details:*
- Name: ${formData.name}
- Phone: ${phone}
- Age: ${formData.age || 'Not specified'}

🚨 *Urgent Problem:*
- Issue: ${formData.problem}
- Pain Level (1-10): ${formData.painLevel || 'High'}
- Preferred Date: ${formData.preferredDate || 'ASAP'}
- Preferred Time: ${formData.preferredTime || 'Any time'}
- Location: ${formData.location || 'Will visit clinic'}

Please prioritize this request. Thank you! 🙏`;
        break;

      case 'emergency':
        message = `🆘 *MEDICAL EMERGENCY*
URGENT: This is a medical emergency requiring immediate physiotherapy intervention.

👤 *Patient Details:*
- Name: ${formData.name}
- Phone: ${phone}
- Age: ${formData.age || 'Not specified'}

⚡ *Emergency Details:*
- Current Situation: ${formData.emergency}
- Location: ${formData.location}
- Can travel to clinic: ${formData.preferredTime || 'Not specified'}

Please respond immediately or call directly. Thank you!`;
        break;

      case 'consultation':
        message = `💬 *FREE CONSULTATION REQUEST*
Hi Dr. Pritish! I'm interested in physiotherapy treatment and need guidance.

👤 *Patient Details:*
- Name: ${formData.name}
- Phone: ${phone}
- Age: ${formData.age || 'Not specified'}

📋 *My Situation:*
- Problem: ${formData.problem}
- Budget Range: ${formData.budget || 'Need guidance'}
- Availability: ${formData.availability || 'Flexible'}
- Questions: ${formData.questions || 'General consultation needed'}

Could you please advise the best treatment option for me? Thank you!`;
        break;

      case 'inquiry':
        message = `❓ *GENERAL INQUIRY*
Hi Dr. Pritish! I have some questions about physiotherapy treatment.

👤 *Contact Details:*
- Name: ${formData.name}
- Phone: ${phone}
- Location: ${formData.location || 'Will visit clinic'}

🤔 *My Questions:*
${formData.questions || 'General inquiry about treatments and services'}

Thank you for your time! 😊`;
        break;

      default:
        message = `📞 *CONTACT REQUEST*
Hi Dr. Pritish!

👤 *Contact Details:*
- Name: ${formData.name}
- Phone: ${phone}

${formData.problem ? `Details: ${formData.problem}` : ''}

Thank you!`;
    }

    // Call the onSubmit callback with the formatted message
    onSubmit(message);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      phone: '',
      age: '',
      problem: '',
      painLevel: '',
      preferredDate: '',
      preferredTime: '',
      location: '',
      previousTreatment: '',
      emergency: '',
      budget: '',
      availability: '',
      questions: ''
    });
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="patient-info-modal">
      <Modal.Header className="bg-gradient-primary text-white">
        <Modal.Title className="d-flex align-items-center">
          <FaUser className="me-2" />
          {title}
        </Modal.Title>
        <Button variant="link" className="text-white p-0" onClick={handleClose}>
          <FaTimes />
        </Button>
      </Modal.Header>

      <Modal.Body className="p-4">
        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  <FaUser className="me-1 text-primary" />
                  Full Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  isInvalid={!!errors.name}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  <FaPhone className="me-1 text-primary" />
                  Phone Number *
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit phone number"
                  isInvalid={!!errors.phone}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Age</Form.Label>
                <Form.Control
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="Enter your age"
                  min="1"
                  max="120"
                />
              </Form.Group>
            </Col>
            {(type === 'emergency' || type === 'inquiry') && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    <FaMapMarkerAlt className="me-1 text-primary" />
                    Location {type === 'emergency' ? '*' : ''}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Your current location/area"
                    isInvalid={!!errors.location}
                    required={type === 'emergency'}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.location}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </Row>

          {/* Type-specific fields */}
          {type === 'emergency' && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold text-danger">
                    <FaNotesMedical className="me-1" />
                    Emergency Situation *
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="emergency"
                    value={formData.emergency}
                    onChange={handleInputChange}
                    placeholder="Please describe the emergency situation in detail"
                    isInvalid={!!errors.emergency}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.emergency}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          )}

          {(type === 'regular' || type === 'urgent' || type === 'consultation') && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    <FaNotesMedical className="me-1 text-primary" />
                    {type === 'consultation' ? 'Your Situation *' : 'Problem Description *'}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="problem"
                    value={formData.problem}
                    onChange={handleInputChange}
                    placeholder={type === 'consultation' 
                      ? "Describe your condition and what guidance you need"
                      : "Describe your problem/pain area (e.g., lower back pain, knee injury, etc.)"
                    }
                    isInvalid={!!errors.problem}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.problem}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          )}

          {type === 'inquiry' && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    <FaNotesMedical className="me-1 text-primary" />
                    Your Questions
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="questions"
                    value={formData.questions}
                    onChange={handleInputChange}
                    placeholder="What would you like to know about our physiotherapy services?"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {(type === 'regular' || type === 'urgent') && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Pain Level (1-10)</Form.Label>
                    <Form.Select
                      name="painLevel"
                      value={formData.painLevel}
                      onChange={handleInputChange}
                    >
                      <option value="">Select pain level</option>
                      <option value="1-2">1-2 (Mild discomfort)</option>
                      <option value="3-4">3-4 (Noticeable pain)</option>
                      <option value="5-6">5-6 (Moderate pain)</option>
                      <option value="7-8">7-8 (Severe pain)</option>
                      <option value="9-10">9-10 (Extreme pain)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Previous Treatment</Form.Label>
                    <Form.Select
                      name="previousTreatment"
                      value={formData.previousTreatment}
                      onChange={handleInputChange}
                    >
                      <option value="">Select option</option>
                      <option value="No previous treatment">No previous treatment</option>
                      <option value="Physiotherapy">Previous physiotherapy</option>
                      <option value="Medication only">Medication only</option>
                      <option value="Surgery">Had surgery</option>
                      <option value="Other treatment">Other treatment</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      <FaCalendarAlt className="me-1 text-primary" />
                      Preferred Date *
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      isInvalid={!!errors.preferredDate}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.preferredDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Preferred Time</Form.Label>
                    <Form.Select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                    >
                      <option value="">Any time</option>
                      <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
                      <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
                      <option value="Evening (5PM-9PM)">Evening (5PM-9PM)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {type === 'consultation' && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Budget Range</Form.Label>
                  <Form.Select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                  >
                    <option value="">Need guidance</option>
                    <option value="Under ₹2000">Under ₹2000</option>
                    <option value="₹2000-5000">₹2000-5000</option>
                    <option value="₹5000-10000">₹5000-10000</option>
                    <option value="Above ₹10000">Above ₹10000</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Your Availability</Form.Label>
                  <Form.Control
                    type="text"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    placeholder="e.g., Weekdays after 6PM, Weekends"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {type === 'consultation' && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Additional Questions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="questions"
                    value={formData.questions}
                    onChange={handleInputChange}
                    placeholder="Any specific questions about treatment, costs, or procedures?"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Form>

        <Alert variant="info" className="mt-3">
          <small>
            📱 Your information will be sent via WhatsApp to Dr. Pritish for booking confirmation.
            Please ensure your details are correct.
          </small>
        </Alert>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="d-flex align-items-center"
        >
          <FaWhatsapp className="me-2" />
          {isSubmitting ? 'Sending...' : 'Send via WhatsApp'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PatientInfoModal;